/**
 * CurrentPeriodPaymentCard
 * Shows external payment status for the current billing period on the lease overview tab
 */

import type { PaymentConfig } from '../../lib/api';
import type { LeaseMatchingSummary } from '../../api/external-payments-hub.api';

interface CurrentPeriodPaymentCardProps {
  paymentConfig: PaymentConfig;
  matchingSummary: LeaseMatchingSummary | null;
  onConfigUpdated?: (config: PaymentConfig) => void; // kept for API compatibility
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function getPeriodStatusLabel(
  matchStatus: 'none' | 'suggested' | 'confirmed' | 'auto_matched' | null
): { label: string; colorClass: string } {
  switch (matchStatus) {
    case 'auto_matched':
      return { label: 'Auto-applied', colorClass: 'text-success' };
    case 'confirmed':
      return { label: 'Payment Confirmed', colorClass: 'text-success' };
    case 'suggested':
      return { label: 'Pending Review', colorClass: 'text-warning' };
    case 'none':
    case null:
    default:
      return { label: 'No Payment Detected', colorClass: 'text-muted-foreground' };
  }
}

export function CurrentPeriodPaymentCard({
  paymentConfig,
  matchingSummary,
}: CurrentPeriodPaymentCardProps) {
  if (paymentConfig.paymentMethod !== 'external') return null;
  const phase = paymentConfig.externalPhase;

  const periodStatus = matchingSummary?.currentPeriodStatus ?? null;
  const statusInfo = getPeriodStatusLabel(periodStatus?.matchStatus ?? null);
  const expectedAmount = periodStatus?.expectedAmount ?? null;

  return (
    <div
      className="bg-card rounded-lg border border-border p-4"
      data-testid="current-period-payment-card"
    >
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-foreground m-0">
          External Payments
        </h4>
        {phase === 'learning' ? (
          <span
            className="text-xs font-medium text-warning bg-warning/10 border border-warning/30 px-2 py-0.5 rounded"
            data-testid="external-mode-badge"
          >
            Learning
          </span>
        ) : (
          <span
            className="text-xs font-medium text-success bg-success/10 border border-success/30 px-2 py-0.5 rounded"
            data-testid="external-mode-badge"
          >
            Active
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2">
        {expectedAmount != null && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Expected</span>
            <span className="font-medium">{formatCurrency(expectedAmount)}</span>
          </div>
        )}

        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Status</span>
          <span className={`font-medium ${statusInfo.colorClass}`} data-testid="period-payment-status">
            {statusInfo.label}
          </span>
        </div>

        {periodStatus?.detectedPayment && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Detected</span>
            <span className="font-medium">
              {formatCurrency(periodStatus.detectedPayment.amount)}
            </span>
          </div>
        )}
      </div>

      {phase === 'learning' && (
        <p
          className="mt-3 text-xs text-muted-foreground"
          data-testid="monitoring-mode-note"
        >
          The system is learning your tenants&apos; payment patterns. Confirmed matches will record payments; auto-matches are held until promotion.
        </p>
      )}
    </div>
  );
}
